import { createRouter, createWebHistory } from 'vue-router'
import { appConfig } from '@/config/appConfig'
import { isAuthenticated } from '@/composables/useAuth'
import { useAuthStore } from '@/stores/auth'
import { logger } from '@/services/logger'

// Routes render bare (no app shell) and are skipped at the top of the global guard.
const NO_LAYOUT_ROUTE_NAMES = new Set(['error', 'unauthorized', 'not-found'])

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Phase 2: temporary redirect to the first shippable screen. Becomes the real
    // OpsDashboard landing in Phase 3 (the dashboard view file already exists).
    {
      path: '/',
      redirect: '/tenants',
    },
    {
      path: '/tenants',
      name: 'tenants',
      component: () => import('@/views/tenants/TenantListView.vue'),
    },
    {
      path: '/tenants/:uid',
      name: 'tenant-detail',
      component: () => import('@/views/tenants/TenantDetailView.vue'),
    },
    {
      path: '/rollouts',
      name: 'rollouts',
      component: () => import('@/views/common/ComingSoonView.vue'),
    },
    {
      path: '/rollouts/:migrationId',
      name: 'rollout-detail',
      component: () => import('@/views/common/ComingSoonView.vue'),
    },
    {
      path: '/resources',
      name: 'resource-state',
      component: () => import('@/views/common/ComingSoonView.vue'),
    },
    {
      path: '/reconcile',
      name: 'reconcile',
      component: () => import('@/views/common/ComingSoonView.vue'),
    },
    {
      path: '/infrastructure',
      name: 'infrastructure',
      component: () => import('@/views/common/ComingSoonView.vue'),
    },
    {
      path: '/audit-log',
      name: 'audit-log',
      component: () => import('@/views/common/ComingSoonView.vue'),
    },
    {
      path: '/users',
      // Keep the existing route name so any deep-links/guards stay valid; only
      // the path and target component change from the ComingSoon stub.
      name: 'admin-users',
      component: () => import('@/views/users/UserListView.vue'),
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('@/views/unauthorized/UnauthorizedView.vue'),
    },
    {
      path: '/error',
      name: 'error',
      component: () => import('@/views/error/ErrorView.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/error/NotFoundView.vue'),
    },
  ],
})

// Single global guard — the only auth gate (no per-route role meta). NO_LAYOUT
// routes render without authentication so the operator can always reach the
// error / unauthorized / not-found states. Keycloak is initialized once in main.ts
// before mount, so this guard only reads the session state and hydrates the store —
// it must never re-init (re-init during navigation caused a login-required loop).
router.beforeEach((to) => {
  if (NO_LAYOUT_ROUTE_NAMES.has(to.name as string)) return true

  const authStore = useAuthStore()

  // Mock mode: initAuth seeded a fake operator in main.ts — just hydrate and proceed.
  if (appConfig.useMocks()) {
    authStore.hydrate()
    return true
  }

  // Real mode: a failed init or a blank operator realm both leave the session
  // unauthenticated, so this single check covers auth failure and misconfiguration.
  if (!isAuthenticated()) {
    logger.error('Operator is not authenticated — routing to error')
    return { name: 'error' }
  }

  authStore.hydrate()
  return true
})

export default router
